import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  useTheme,
  useMediaQuery,
  Popover,
  Grid,
  Chip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { CalendarViewType, TimeScale, VIEW_TYPE_LABELS, TIME_SCALE_LABELS, TIME_SCALE_OPTIONS } from '../types/calendar.types';
import { SCHEDULE_LINK_LEGEND_COLORS } from '../utils/dateTypeColors';

interface CalendarToolbarProps {
  viewType: CalendarViewType;
  dayViewDays: number;
  currentDate: Date;
  timeScale: TimeScale;
  onViewTypeChange: (type: CalendarViewType) => void;
  onDayViewDaysChange: (days: number) => void;
  onTimeScaleChange: (scale: TimeScale) => void;
  onGoToToday: () => void;
  onGoPrevious: () => void;
  onGoNext: () => void;
  onDateSelect: (date: Date) => void;
  dateRangeLabel: string;
}

// Helper to check if a date is today
const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// Helper to check if current view includes today
const viewIncludesToday = (currentDate: Date, viewType: CalendarViewType, dayViewDays: number): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const viewStart = new Date(currentDate);
  viewStart.setHours(0, 0, 0, 0);
  
  if (viewType === 'month') {
    return viewStart.getMonth() === today.getMonth() && viewStart.getFullYear() === today.getFullYear();
  }
  
  if (viewType === 'week') {
    const weekStart = new Date(viewStart);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return today >= weekStart && today <= weekEnd;
  }
  
  if (viewType === 'workweek') {
    const weekStart = new Date(viewStart);
    const dayOfWeek = weekStart.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    weekStart.setDate(weekStart.getDate() + diff);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 4);
    return today >= weekStart && today <= weekEnd;
  }
  
  // Day view
  const viewEnd = new Date(viewStart);
  viewEnd.setDate(viewEnd.getDate() + dayViewDays - 1);
  return today >= viewStart && today <= viewEnd;
};

export const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  viewType,
  dayViewDays,
  currentDate,
  timeScale,
  onViewTypeChange,
  onDayViewDaysChange,
  onTimeScaleChange,
  onGoToToday,
  onGoPrevious,
  onGoNext,
  onDateSelect,
  dateRangeLabel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for date picker popover
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const viewTypes: CalendarViewType[] = ['day', 'week', 'workweek', 'month'];
  
  // Check if current view includes today
  const showGoToToday = useMemo(() => {
    return !viewIncludesToday(currentDate, viewType, dayViewDays);
  }, [currentDate, viewType, dayViewDays]);

  const handleDatePickerOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (date: Date) => {
    onDateSelect(date);
    handleDatePickerClose();
  };

  // State for the picker's displayed month
  const [pickerMonth, setPickerMonth] = useState(new Date());
  
  // When popover opens, set picker month to current date
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPickerMonth(new Date(currentDate));
    setAnchorEl(event.currentTarget);
  };

  // Generate calendar days for the picker
  const calendarDays = useMemo(() => {
    const year = pickerMonth.getFullYear();
    const month = pickerMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days: (Date | null)[] = [];
    
    // Add padding for days before the first of the month
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Add the days of the month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, [pickerMonth]);

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  
  const navigatePickerMonth = (direction: number) => {
    setPickerMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
    {/* Main toolbar row */}
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        p: 1,
      }}
    >
      {/* Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
         {/* Go to Today button - shown when not viewing today */}
         {showGoToToday && (
          <Tooltip title="Go to Today">
            <Button
              size="small"
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={onGoToToday}
              sx={{ ml: 1, textTransform: 'none' }}
            >
              Today
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Previous">
          <IconButton onClick={onGoPrevious} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Pick a date">
          <IconButton onClick={handlePopoverOpen} size="small">
            <CalendarMonthIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Next">
          <IconButton onClick={onGoNext} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>

       
      </Box>



      {/* Date Picker Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleDatePickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          {/* Month/Year Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton size="small" onClick={() => navigatePickerMonth(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {pickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton size="small" onClick={() => navigatePickerMonth(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Week days header */}
          <Grid container spacing={0}>
            {weekDays.map((day) => (
              <Grid item xs={12/7} key={day}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    fontWeight: 600,
                    color: 'text.secondary',
                    py: 0.5,
                  }}
                >
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar days */}
          <Grid container spacing={0}>
            {calendarDays.map((day, index) => (
              <Grid item xs={12/7} key={index}>
                {day ? (
                  <Box
                    onClick={() => handleDateChange(day)}
                    sx={{
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      mx: 'auto',
                      my: 0.25,
                      bgcolor: isToday(day) ? 'primary.main' : 'transparent',
                      color: isToday(day) ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        bgcolor: isToday(day) ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="body2">{day.getDate()}</Typography>
                  </Box>
                ) : (
                  <Box sx={{ width: 32, height: 32, mx: 'auto', my: 0.25 }} />
                )}
              </Grid>
            ))}
          </Grid>

          {/* Quick navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Button
              size="small"
              onClick={() => {
                handleDateChange(new Date());
              }}
            >
              Today
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Date range label */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: isMobile ? '1rem' : '1.25rem',
          minWidth: 150,
          textAlign: 'center',
        }}
      >
        {dateRangeLabel}
      </Typography>
      {/* Legend: blue = Scheduled Customer, green = Work Order Created */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1 }}>
        <Chip
          size="small"
          label="Scheduled Customer"
          sx={SCHEDULE_LINK_LEGEND_COLORS.customerSchedule}
          variant="outlined"
        />
        <Chip
          size="small"
          label="Work Order Created"
          sx={SCHEDULE_LINK_LEGEND_COLORS.withWorkOrder}
          variant="outlined"
        />
      </Box>
      <Box sx={{ flex: 1 }} />

      {/* View type selector */}
      <ButtonGroup size="small" variant="outlined">
        {viewTypes.map((type) => (
          <Button
            key={type}
            
            onClick={() => onViewTypeChange(type)}
            variant={viewType === type ? 'contained' : 'outlined'}
            sx={{ minWidth: 70, textTransform: 'capitalize' }}
          >
            {VIEW_TYPE_LABELS[type]}
          </Button>
        ))}
      </ButtonGroup>

      {/* Day view days selector (only when in day view) */}
      {viewType === 'day' && (
        <FormControl size="small" sx={{ minWidth: 70 }}>
          <InputLabel>Days</InputLabel>
          <Select
            value={dayViewDays}
            onChange={(e) => onDayViewDaysChange(Number(e.target.value))}
            label="Days"
          >
            {[1, 2, 3, 4, 5, 6, 7].map((days) => (
              <MenuItem key={days} value={days}>
                {days} day{days > 1 ? 's' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Time scale selector (hidden in month view) */}
      {viewType !== 'month' && (
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>Scale</InputLabel>
          <Select
            value={timeScale}
            onChange={(e) => onTimeScaleChange(Number(e.target.value) as TimeScale)}
            label="Scale"
          >
            {TIME_SCALE_OPTIONS.map((scale) => (
              <MenuItem key={scale} value={scale}>
                {TIME_SCALE_LABELS[scale]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
    </Box>
  );
};
